<template>
  <span
    v-for="item in items"
    :key="item.key"
    v-tooltip="item.tooltip"
    class="
      sf-pointer
      hover:tw-bg-sf-neutral-lighter-2
      active:tw-bg-sf-neutral-lighter-1
      tw-text-center
      tw-p-2
      tw-text-sf-gray-light
      tw-rounded-md
      tw-leading-4
      tw-transition-all
      tw-hidden
      group-hover:tw-inline
    "
    :data-heap="item.heapTag"
    :data-testid="`remark-action-${item.key}`"
    @click="(event) => handleClick(item.key, event)"
  >
    <span
      v-if="item.icon"
      class="tw-text-base tw-min-w-4 tw-w-4 tw-min-h-4 tw-h-4 tw-leading-none"
      :class="`fa fa-${item.icon}`"
    />
  </span>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type { RemarkActions } from './types'

const I18n = useI18n()

const emit =
  defineEmits<{
    click: [RemarkActions, MouseEvent]
  }>()

interface Props {
  allowedActions?: {
    [N in RemarkActions]?: boolean
  }
  readHeapTag?: string
  read?: boolean
}

const POSSIBLE_ACTIONS = [
  'updateTags',
  'edit',
  'remove',
  'toggleRead',
  'openInContext',
] as RemarkActions[]

const props = withDefaults(defineProps<Props>(), {
  allowedActions: () => ({
    updateTags: false,
    edit: false,
    remove: false,
    toggleRead: false,
    openInContext: false,
  }),
})

const items = computed(() => {
  const result = []
  for (const key of POSSIBLE_ACTIONS) {
    if (props.allowedActions[key]) {
      result.push({
        key,
        icon: getIconPerAction(key),
        tooltip: getTooltipPerAction(key),
        heapTag: getHeapTagPerAction(key),
      })
    }
  }
  return result
})

function getTooltipPerAction(action: RemarkActions): string | undefined {
  switch (action) {
    case 'updateTags':
      return I18n.t('communication.cards.edit_tags')
    case 'edit':
      return I18n.t('common.edit')
    case 'remove':
      return I18n.t('common.remove')
    case 'toggleRead':
      return I18n.t(
        `components.notification.single.toggle_read_tooltip.${
          props.read || false
        }`
      )
    case 'openInContext':
      return I18n.t('communication.cards.open_in_context')
  }
}

function getIconPerAction(action: RemarkActions): string | undefined {
  switch (action) {
    case 'updateTags':
      return 'tag'
    case 'edit':
      return 'pencil'
    case 'remove':
      return 'trash'
    case 'toggleRead':
      return props.read ? 'envelope' : 'envelope-open'
    case 'openInContext':
      return 'external-link'
  }
}

function getHeapTagPerAction(action: RemarkActions): string | undefined {
  if (action === 'toggleRead') {
    return props.readHeapTag
  }
}

function handleClick(action: RemarkActions, event: MouseEvent) {
  emit('click', action, event)
}
</script>
